import { Box, lighten } from '@mui/material';
import type { ProjectSchemaJSON } from '@takeshape/schema';
import { type Diff, diff } from 'deep-diff';
import map from 'lodash/map';
import type React from 'react';
import { useMemo } from 'react';
import { SchemaDiffItem } from '../schema-diff-item';

export type ProjectSchemaDiffLine = Diff<ProjectSchemaJSON, ProjectSchemaJSON>;

export type ProjectSchemaDiff = ProjectSchemaDiffLine[];

export type SchemaDiffProps = {
  readonly before: ProjectSchemaJSON | null;
  readonly after: ProjectSchemaJSON | null;
};

export type DiffItem = {
  kind: 'N' | 'E' | 'A' | 'D';
  label: string;
};

const normalSections = ['shapes', 'queries', 'mutations', 'forms', 'services', 'indexedShapes'] as const;

type DiffItems = {
  other: DiffItem[];
} & Record<(typeof normalSections)[number], Record<string, DiffItem[]>>;

export const SchemaDiff: React.FC<SchemaDiffProps> = (props) => {
  const { before, after } = props;

  const diffItems = useMemo(() => {
    const result: DiffItems = {
      shapes: {},
      queries: {},
      mutations: {},
      forms: {},
      services: {},
      indexedShapes: {},
      other: []
    };

    if (before && after) {
      const diffLines = diff(before, after);

      if (diffLines) {
        for (const line of diffLines) {
          if (!line.path) {
            continue;
          }

          const section = line.path?.[0] as (typeof normalSections)[number];
          if (normalSections.includes(section)) {
            const name = line.path?.[1] || section;
            result[section][name] ||= [];

            result[section][name].push({
              kind: line.kind,
              label: line.path.slice(2).join('.')
            });
          } else {
            result.other.push({
              label: line.path.join(),
              kind: line.kind
            });
          }
        }
      } else {
        return null;
      }
    }

    return result;
  }, [before, after]);

  if (!diffItems) {
    return (
      <Box
        sx={{
          borderRadius: 1,
          p: 1,
          my: 1,
          bgcolor: (theme) => lighten(theme.palette.primary.light, 0.6)
        }}
      >
        No differences.
      </Box>
    );
  }

  return (
    <div>
      {normalSections.map((section) =>
        map(diffItems[section], (items, name) => {
          if (items.length === 1 && items[0].label === '') {
            return (
              <SchemaDiffItem
                section={section}
                diffItem={{
                  label: name,
                  kind: items[0].kind
                }}
              />
            );
          }

          return (
            <div>
              <SchemaDiffItem
                section={section}
                diffItem={{
                  label: name,
                  kind: 'E'
                }}
              />
              <Box sx={{ ml: 4 }}>
                {items.map((item) => (
                  <div key={item.label}>
                    <SchemaDiffItem section="other" diffItem={item} />
                  </div>
                ))}
              </Box>
            </div>
          );
        })
      )}
      {diffItems.other.map((item) => (
        <SchemaDiffItem key={item.label} section="other" diffItem={item} />
      ))}
    </div>
  );
};
